import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { TbLoader2 } from "react-icons/tb";

const buttonVariants = cva(
  "inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        none: "",
        default: "text-muted-foreground hover:text-foreground hover:bg-ghost",
        pronounced: "text-foreground hover:text-foreground hover:bg-ghost/50",
        opaque: "text-primary bg-background hover:bg-secondary",
        background:
          "bg-primary-foreground text-primary/80 shadow hover:bg-primary/10",
        invert: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground active:bg-accent/70",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        midnight: "bg-midnight text-midnight-foreground hover:bg-midnight/80",
        azure: "bg-azure text-azure-foreground hover:bg-azure/80",
        azureOutline:
          "bg-primary-foreground text-azure border-azure border hover:text-azure/80",
        aqua: "bg-aqua text-aqua-foreground hover:bg-aqua/80",
        "muted-alt-ghost":
          "hover:bg-muted-alt/30 hover:text-muted-alt-foreground",
        ghost:
          "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-success text-success-foreground hover:bg-success/80",
        successOutline:
          "bg-success-foreground text-success border-success border hover:text-success/80",

        error: "bg-error text-error-foreground hover:bg-error/80",
        errorOutline:
          "bg-error-foreground text-error border-error border hover:text-error/80",
      },
      size: {
        default: "h-8 px-2 py-2 text-sm",
        "2xs": "h-5 rounded-sm text-xs",
        xs: "h-6 rounded-sm text-xs",
        sm: "h-7 rounded-md text-xs",
        lg: "h-9 rounded-md px-2",
        "2xl": "h-12 rounded-xl text-lg px-4",
        icon: "h-8 w-8",
        page: "h-7 w-7",
        custom: "",
      },
      invert: {
        false: "",
        true: "",
      },
    },
    compoundVariants: [
      {
        variant: "azure",
        invert: true,
        className: "bg-azure/10 text-azure hover:bg-azure/20",
      },
      {
        variant: "destructive",
        invert: true,
        className: "bg-destructive/10 text-destructive hover:bg-destructive/20",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      invert: false,
    },
  },
);

interface ButtonPropsBase
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export type ButtonProps = ButtonPropsBase &
  (
    | { asChild: true }
    | {
        asChild?: false;
        loading?: boolean;
        leftSection?: JSX.Element;
        rightSection?: JSX.Element;
      }
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, invert, children, ...props }, ref) => {
    if (props.asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, invert, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    const {
      loading = false,
      leftSection,
      rightSection,
      disabled,
      ...otherProps
    } = props;

    return (
      <button
        className={cn(buttonVariants({ variant, size, invert, className }))}
        disabled={loading || disabled}
        ref={ref}
        {...otherProps}
      >
        {((leftSection && loading) ||
          (!leftSection && !rightSection && loading)) && (
          <TbLoader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        {!loading && leftSection && <div className="mr-2">{leftSection}</div>}
        {children}
        {!loading && rightSection && <div className="ml-2">{rightSection}</div>}
        {rightSection && loading && (
          <TbLoader2 className="ml-2 h-4 w-4 animate-spin" />
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
